import React from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { required } from 'redux-form-validators';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { isValidNumber } from 'libphonenumber-js';
import { compose } from 'redux';

import { FormMessageHOC } from 'common';
import {
  ReduxSelectField,
  ReduxTextField,
  PhoneField,
  CheckboxField,
  DateField,
  DefaultButton,
} from 'common/components';
import { withTheme } from 'common/styling/theme';

import { sendKYC } from '../../_redux';
import { ExperienceFields, SelectCountryField } from './fields';

import { staticStyles, getDynamicStyles } from './style';

const validatePhoneNumber = phoneNumber => {
  if (phoneNumber && !isValidNumber(phoneNumber)) {
    return <FormattedMessage id="validationErrorWrongPhone" />;
  }
};

const KYCForm = ({
  handleSubmit,
  submitting,
  invalid,
  error,
  user,
  countries,
  states,
  countriesAreLoaded,
  statesAreLoaded,
  experienceForex,
  experienceDerivative,
  experienceSecurities,
  experienceInvestmentFund,
  mandatoryDocumentsAreSending,
  removeKycTaxCheckbox,
  personalIdInKycForm,
  kycFormWithoutPhone,
  kycFormSettings,
  dispatch,
  intl,
  theme,
}) => {
  const submit = handleSubmit(formValues => {
    if (removeKycTaxCheckbox) {
      formValues = { ...formValues, usTax: true };
    }
    return sendKYC(formValues, dispatch);
  });

  const countriesOptions = countries
    .map(option => ({
      value: option.gid,
      label: option.name,
    }))
    .sort((a, b) => (a.label > b.label ? 1 : -1));

  const statesOptions = states
    .map(state => ({
      value: state.gid,
      label: state.name,
    }))
    .sort((a, b) => (a.label > b.label ? 1 : -1));

  const generateOptionsFromTranslations = keys =>
    keys.map(key => ({ value: key, label: intl.formatMessage({ id: key }) }));
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const isSomeSectionCanEditable =
    kycFormSettings &&
    Object.values(kycFormSettings).some(
      ({ visible, editable_after_user_verification: editable }) => editable && visible
    );

  const isSubmitButtonVisible = user.status !== 'verified' || isSomeSectionCanEditable;

  const isSectionRequired = sectionName => {
    const section = kycFormSettings?.[sectionName];

    if (!section) {
      if (sectionName === 'experience') {
        return [];
      }

      return [required()];
    }

    if (section.required && !(user.status === 'verified' && !section.editable_after_user_verification)) {
      return [required()];
    }

    return [];
  };

  const isSectionVisible = sectionName => {
    if (!kycFormSettings) {
      return true;
    }

    return kycFormSettings[sectionName]?.visible;
  };

  const isFieldDisabled = sectionName => {
    const initialDisabled = user.status === 'verified' || submitting;

    if (!kycFormSettings) {
      return initialDisabled;
    }

    const section = kycFormSettings[sectionName];

    if (!section) {
      return initialDisabled;
    }

    return (user.status === 'verified' && !section.editable_after_user_verification) || submitting;
  };

  return (
    <form className="KYCForm" onSubmit={submit}>
      {isSectionVisible('generalInfo') && (
        <div className="KYCForm__inputs-inner">
          <FormattedMessage id="generalInfo">{txt => <h2>{txt}</h2>}</FormattedMessage>

          <div className="KYCForm__inputs-block">
            <Field
              component={ReduxTextField}
              type="text"
              name="firstName"
              textId="kycFirstName"
              disabled={isFieldDisabled('generalInfo')}
              validate={isSectionRequired('generalInfo')}
            />
            <Field
              component={ReduxTextField}
              type="text"
              name="middleName"
              textId="kycMiddleName"
              disabled={isFieldDisabled('generalInfo')}
            />
            <Field
              component={ReduxTextField}
              type="text"
              name="lastName"
              textId="kycLastName"
              disabled={isFieldDisabled('generalInfo')}
              validate={isSectionRequired('generalInfo')}
            />
          </div>

          <div className={classNames('KYCForm__inputs-block', { 'block-two-inp': !personalIdInKycForm })}>
            <Field
              component={ReduxSelectField}
              name="taxResidence"
              textId="kycTaxResidence"
              options={countriesOptions}
              disabled={isFieldDisabled('generalInfo')}
              validate={isSectionRequired('generalInfo')}
              searchable
            />
            <Field
              component={DateField}
              type="date"
              name="birthday"
              textId="kycBirthday"
              placeholder="dd.mm.yyyy"
              disabled={isFieldDisabled('generalInfo')}
              validate={isSectionRequired('generalInfo')}
            />
            {personalIdInKycForm && (
              <Field
                component={ReduxTextField}
                type="text"
                name="personalId"
                textId="kycPassportNumber"
                disabled={isFieldDisabled('generalInfo')}
              />
            )}
          </div>

          <div className="KYCForm__inputs-block block-two-inp">
            <Field
              component={ReduxSelectField}
              name="nationality"
              textId="kycNationality"
              options={countriesOptions}
              disabled={isFieldDisabled('generalInfo')}
              validate={isSectionRequired('generalInfo')}
              searchable
            />
            {!kycFormWithoutPhone && (
              <Field
                component={PhoneField}
                name="phone"
                textId="kycPhone"
                disabled={isFieldDisabled('generalInfo') || user.phoneConfirmed}
                validate={[...isSectionRequired('generalInfo'), validatePhoneNumber]}
              />
            )}
          </div>

          {!removeKycTaxCheckbox && (
            <Field component={CheckboxField} name="usTax" textId="kycUsTax" disabled={isFieldDisabled('generalInfo')} />
          )}
        </div>
      )}

      {isSectionVisible('address') && (
        <div className="KYCForm__inputs-inner">
          <FormattedMessage id="kycAddress">{txt => <h2>{txt}</h2>}</FormattedMessage>

          <div className="KYCForm__inputs-block block-two-inp">
            <Field
              component={SelectCountryField}
              name="address.country"
              textId="kycCountry"
              options={countriesOptions}
              disabled={isFieldDisabled('address') || !countriesAreLoaded}
              validate={isSectionRequired('address')}
            />
            <Field
              component={ReduxSelectField}
              name="address.state"
              textId="kycState"
              options={statesOptions}
              disabled={isFieldDisabled('address') || !statesAreLoaded}
              validate={isSectionRequired('address')}
              searchable
            />
          </div>

          <div className="KYCForm__inputs-block">
            <Field
              component={ReduxTextField}
              type="text"
              name="address.city"
              textId="kycCity"
              disabled={isFieldDisabled('address')}
              validate={isSectionRequired('address')}
            />
            <Field
              component={ReduxTextField}
              type="text"
              name="address.address"
              textId="kycAddress"
              disabled={isFieldDisabled('address')}
              validate={isSectionRequired('address')}
            />
            <Field
              component={ReduxTextField}
              type="text"
              name="address.postCode"
              textId="kycPostCode"
              disabled={isFieldDisabled('address')}
              validate={isSectionRequired('address')}
            />
          </div>
        </div>
      )}

      {isSectionVisible('finance') && (
        <div className="KYCForm__inputs-inner">
          <FormattedMessage id="kycFinDetails">{txt => <h2>{txt}</h2>}</FormattedMessage>

          <div className="KYCForm__inputs-block">
            <Field
              component={ReduxSelectField}
              name="finance.capital"
              textId="kycNetCapital"
              options={generateOptionsFromTranslations([
                'kycAmountBelow10',
                'kycAmount10to50',
                'kycAmount50to100',
                'kycAmountOver100',
              ])}
              disabled={isFieldDisabled('finance')}
              validate={isSectionRequired('finance')}
            />
            <Field
              component={ReduxSelectField}
              name="finance.income"
              textId="kycAnnualIncome"
              options={generateOptionsFromTranslations([
                'kycAmountBelow10',
                'kycAmount10to50',
                'kycAmount50to100',
                'kycAmountOver100',
              ])}
              disabled={isFieldDisabled('finance')}
              validate={isSectionRequired('finance')}
            />
            <Field
              component={ReduxSelectField}
              name="finance.commitments"
              textId="kycMonFinCommit"
              options={generateOptionsFromTranslations([
                'kycCommitmentsBelow20',
                'kycCommitments21to40',
                'kycCommitments41to60',
                'kycCommitments61to80',
                'kycCommitmentsOver80',
              ])}
              disabled={isFieldDisabled('finance')}
              validate={isSectionRequired('finance')}
            />
          </div>

          <div className="KYCForm__inputs-block block-two-inp">
            <Field
              component={ReduxTextField}
              type="text"
              name="finance.investments"
              textId="kycApproxInvestPerAnn"
              disabled={isFieldDisabled('finance')}
              validate={isSectionRequired('finance')}
            />
            <Field
              component={ReduxSelectField}
              name="finance.purpose"
              textId="kycPurpose"
              options={generateOptionsFromTranslations([
                'kycPurposeInvestment',
                'kycPurposeHedging',
                'kycPurposeSpecTrading',
              ])}
              disabled={isFieldDisabled('finance')}
              validate={isSectionRequired('finance')}
            />
          </div>
        </div>
      )}

      {isSectionVisible('activity') && (
        <div className="KYCForm__inputs-inner">
          <FormattedMessage id="kycTypeOfActivity">{txt => <h2>{txt}</h2>}</FormattedMessage>

          <div className="KYCForm__inputs-block block-two-inp">
            <Field
              component={ReduxSelectField}
              name="activity.employment"
              textId="kycEmplStatus"
              options={generateOptionsFromTranslations([
                'kycEmploymentEmpl',
                'kycEmploymentUnempl',
                'kycEmploymentSelfEmpl',
                'kycEmploymentRet',
                'kycEmploymentStud',
              ])}
              disabled={isFieldDisabled('activity')}
              validate={isSectionRequired('activity')}
            />
            <Field
              component={ReduxSelectField}
              name="activity.source"
              textId="kycSourceOfFunds"
              options={generateOptionsFromTranslations([
                'kycSourceProfAct',
                'kycSourceSavings',
                'kycSourceFam',
                'kycSourceInvest',
                'kycSourceBorrow',
                'kycSourcePension',
                'kycSourceOther',
              ])}
              disabled={isFieldDisabled('activity')}
              validate={isSectionRequired('activity')}
            />
          </div>

          <div className="KYCForm__inputs-block block-two-inp">
            <Field
              component={ReduxSelectField}
              name="activity.business"
              textId="kycNatureOfBusiness"
              options={generateOptionsFromTranslations([
                'kycBizAccount',
                'kycBizSecr',
                'kycBizAgric',
                'kycBizArt',
                'kycBizBank',
                'kycBizCatHosp',
                'kycBizMedia',
                'kycBizEdu',
                'kycBizEmerg',
                'kycBizEngin',
                'kycBizHealth',
                'kycBizIT',
                'kycBizLegal',
                'kycBizLeisure',
                'kycBizManuf',
                'kycBizPR',
                'kycBizProp',
                'kycBizSales',
                'kycBizSocial',
                'kycBizTelecom',
                'kycBizTransport',
                'kycBizOther',
              ])}
              disabled={isFieldDisabled('activity')}
              validate={isSectionRequired('activity')}
              searchable
            />
            <Field
              component={ReduxSelectField}
              name="activity.education"
              textId="kycLvlOfEdu"
              options={generateOptionsFromTranslations(['kycEduHS', 'kycEduColl', 'kycEduUnivers', 'kycEduOther'])}
              disabled={isFieldDisabled('activity')}
              validate={isSectionRequired('activity')}
            />
          </div>
        </div>
      )}

      {isSectionVisible('experience') && (
        <div className="KYCForm__inputs-inner">
          <FormattedMessage id="kycTradingExp">{txt => <h2>{txt}</h2>}</FormattedMessage>

          <div className="KYCForm__inputs-block block-experience">
            <ExperienceFields
              prefix="experience.forex"
              textId="kycForexCdfs"
              disabled={isFieldDisabled('experience')}
              validate={isSectionRequired('experience')}
              active={experienceForex}
              generateOptionsFromTranslations={generateOptionsFromTranslations}
            />
          </div>

          <div className="KYCForm__inputs-block block-experience">
            <ExperienceFields
              prefix="experience.derivative"
              textId="kycDerivativeInstr"
              disabled={isFieldDisabled('experience')}
              validate={isSectionRequired('experience')}
              active={experienceDerivative}
              generateOptionsFromTranslations={generateOptionsFromTranslations}
            />
          </div>

          <div className="KYCForm__inputs-block block-experience">
            <ExperienceFields
              prefix="experience.securities"
              textId="kycTransfSec"
              disabled={isFieldDisabled('experience')}
              validate={isSectionRequired('experience')}
              active={experienceSecurities}
              generateOptionsFromTranslations={generateOptionsFromTranslations}
            />
          </div>

          <div className="KYCForm__inputs-block block-experience">
            <ExperienceFields
              prefix="experience.investmentFund"
              textId="kycUnitsCollectiveInv"
              disabled={isFieldDisabled('experience')}
              validate={isSectionRequired('experience')}
              active={experienceInvestmentFund}
              generateOptionsFromTranslations={generateOptionsFromTranslations}
            />
          </div>
        </div>
      )}

      {user.status === 'verified' && !isSomeSectionCanEditable && (
        <div className="KYCForm__inputs-inner KYCForm__text">
          <FormattedMessage
            id="ifUserSettingIsChange"
            values={{
              support: (
                <Link to="/support/" id="cleint-KYCForm-Link-to-support">
                  <FormattedMessage id="contactCustomerSupport" />
                </Link>
              ),
            }}
          />
        </div>
      )}

      {!mandatoryDocumentsAreSending && (
        <div className="KYCForm__inputs-inner KYCForm__text">
          <FormattedMessage id="kycUploadeMandatoryDocuments" />
        </div>
      )}

      {isSubmitButtonVisible && (
        <DefaultButton
          textId="sendToVerify"
          type="submit"
          disabled={invalid || submitting || !mandatoryDocumentsAreSending}
          loading={submitting}
          filled
        />
      )}
      {error && <strong className="formError">{error}</strong>}
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </form>
  );
};

KYCForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  submitting: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  experienceForex: PropTypes.bool.isRequired,
  experienceDerivative: PropTypes.bool.isRequired,
  experienceSecurities: PropTypes.bool.isRequired,
  experienceInvestmentFund: PropTypes.bool.isRequired,
  error: PropTypes.string,
  countries: PropTypes.array.isRequired,
  states: PropTypes.array.isRequired,
  countriesAreLoaded: PropTypes.bool.isRequired,
  statesAreLoaded: PropTypes.bool.isRequired,
  intl: PropTypes.object.isRequired,
  mandatoryDocumentsAreSending: PropTypes.bool.isRequired,
  removeKycTaxCheckbox: PropTypes.bool.isRequired,
  personalIdInKycForm: PropTypes.bool.isRequired,
  kycFormWithoutPhone: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  kycFormSettings: PropTypes.object,
  theme: PropTypes.object,
};

KYCForm.defaultProps = {
  kycFormSettings: null,
  error: '',
  theme: {},
};

const selector = formValueSelector('KYCForm');

const ThemedKYCForm = compose(
  withTheme(),
  FormMessageHOC,
  injectIntl,
  connect(state => ({
    experienceForex: selector(state, 'experience.forex.checkbox') || false,
    experienceDerivative: selector(state, 'experience.derivative.checkbox') || false,
    experienceSecurities: selector(state, 'experience.securities.checkbox') || false,
    experienceInvestmentFund: selector(state, 'experience.investmentFund.checkbox') || false,
    removeKycTaxCheckbox: state.interfaceConfig.features.includes('removeKycTaxCheckbox'),
    personalIdInKycForm: state.interfaceConfig.features.includes('personalIdInKycForm'),
    kycFormWithoutPhone: state.interfaceConfig.features.includes('kycFormWithoutPhone'),
    kycFormSettings: state.interfaceConfig.personalInfoSections,
  })),
  reduxForm({
    form: 'KYCForm',
    onSubmitSuccess(result, dispatch, props) {
      props.showFormSuccessMessage();
    },
  })
)(KYCForm);

export default ThemedKYCForm;
export { KYCForm };
